import { BloodGlucoseObservation } from "@/model/BloodGlucoseObservation";
import Exercise from "@/model/Exercise";
import { Cell, HeaderCell } from "@/screens/patient-details/Cell";
import { intercalcate } from "@/util/array-utils";
import { DateTimeFormatter } from "@js-joda/core";
import { Locale } from "@js-joda/locale_en-us";
import orderBy from "lodash/orderBy";
import { ComponentPropsWithoutRef, Fragment } from "react";
import { titleForContext, titleForQuickCheckReason } from "./Helpers";

type PatientNotesSectionProps = {
  observations: BloodGlucoseObservation[];
} & ComponentPropsWithoutRef<"div">;

const PatientNotesSection = ({
  observations,
  className,
  ...otherProps
}: PatientNotesSectionProps) => {
  const orderedObservations = orderBy(
    observations,
    [(observation) => observation.dateTime.toEpochSecond()],
    ["asc"]
  );

  return (
    <div className={className} {...otherProps}>
      <h2 className="mb-4 text-2xl text-primary/90">Patient notes</h2>
      <div className="grid grid-cols-[min-content_min-content_min-content_1fr] gap-px ps-px">
        <HeaderCell className="justify-start truncate print:break-inside-avoid-page">
          Date
        </HeaderCell>
        <HeaderCell className="justify-start truncate print:break-inside-avoid-page">
          Time
        </HeaderCell>
        <HeaderCell className="justify-start truncate print:break-inside-avoid-page">
          Glucose (mg/dL)
        </HeaderCell>
        <HeaderCell className="justify-start truncate print:break-inside-avoid-page">
          Notes
        </HeaderCell>
        {orderedObservations.map((observation) => {
          const { dateTime, reading, form } = observation;

          const {
            context,
            carbsInGrams,
            proteinInGrams,
            fatInGrams,
            reasons = [],
            exercise,
            notes,
          } = form ?? {};

          const macrosTexts = [
            proteinInGrams === undefined ? null : `${proteinInGrams} g protein`,
            carbsInGrams === undefined ? null : `${carbsInGrams} g carbs`,
            fatInGrams === undefined ? null : `${fatInGrams} g fat`,
          ].filter(Boolean);

          const reasonsTexts = reasons
            .filter((reason) => reason !== "other")
            .map((reason) => titleForQuickCheckReason(reason).toLowerCase());

          const contextTexts = [
            context && titleForContext(context),
            macrosTexts.length > 0 ? `${macrosTexts.join(", ")}` : null,
            reasonsTexts.length > 0 ? `Reason: ${reasonsTexts.join(", ")}` : null,
            exercise ? titleForExercise(exercise).toLowerCase() : null,
          ].filter(Boolean);

          return (
            <Fragment key={`${observation.id}`}>
              <Cell outline className="truncate p-2 print:break-inside-avoid-page">
                {dateFormatter.format(dateTime)}
              </Cell>
              <Cell outline className="truncate p-2 print:break-inside-avoid-page">
                {timeFormatter.format(dateTime)}
              </Cell>
              <Cell outline className="truncate p-2 print:break-inside-avoid-page">
                {reading ? reading.bloodGlucoseInMilligramsPerDeciliter : "N/A"}
              </Cell>
              <Cell outline className="p-2 text-primary/90 print:break-inside-avoid-page">
                <div>
                  {contextTexts.length === 0
                    ? "-"
                    : intercalcate(
                        contextTexts.map((contextText, index) => (
                          <span
                            key={`contextText-${index}`}
                            className="text-ellipsis whitespace-normal"
                          >
                            {contextText}
                          </span>
                        )),
                        (index) => (
                          <span
                            key={`separator-${index}`}
                            className="whitespace-normal px-2 leading-[1.25rem] text-gray-200"
                          >
                            •
                          </span>
                        )
                      )}
                </div>
                {notes && <q className="mt-2 block italic text-primary/90">{notes}</q>}
              </Cell>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

const dateFormatter = DateTimeFormatter.ofPattern("MM/dd/yyyy").withLocale(Locale.US);
const timeFormatter = DateTimeFormatter.ofPattern("h:mm a").withLocale(Locale.US);

const titleForExercise = (exercise: Exercise) => {
  switch (exercise) {
    case "none":
      return "No exercise";
    case "light":
      return "Light exercise";
    case "moderate":
      return "Moderate exercise";
    case "vigorous":
      return "Vigorous exercise";
  }
};

export default PatientNotesSection;
